import { all, put, call, select } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import { push } from 'redux-first-history';
import * as types from '../../constants/ActionTypes';
import Api from '../../functions/Api';
import getQuestById from '../../functions/getQuestById';
import { FirstSteps } from '../../constants/tutorials/TutorialsName';
import CachedTutorial from '../../functions/cachedTutorial';
import { addEvent } from '../../actions/events';
import queryString from '../../functions/URL/parseQuery';
import formQuery from '../../functions/URL/formQuery';
import { fetchProduct, postListedData } from '../../actions/publishing';
import { syncQuest } from './questsSyncSaga';

export function* fetchQuest(action) {
  const { id, isForSync = false } = action;
  const locale = yield select((state) => state.user.locale);

  try {
    yield put({ type: types.REQUEST_QUEST, id });
    const options = {
      headers: {
        'Accept-Language': locale,
      },
    };
    const { data: quest } = yield call(Api.get, `quests/${id}/`, options);
    yield put({
      type: types.RECEIVE_QUEST,
      quest,
    });

    if (!isForSync) {
      yield put({ type: types.REQUEST_CATEGORIES, locale });

      if (quest.products && quest.products.length) {
        yield put(fetchProduct(quest.products[0].id));
      }
    }
  } catch (error) {
    yield put({
      type: types.NOT_RECEIVE_QUEST,
      id,
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* fetchQuests(action = {}) {
  const { page = 1, pageSize = 10 } = action;
  try {
    yield put({ type: types.REQUEST_QUESTS });
    const response = yield call(
      Api.get,
      `v1/quests/${formQuery({ page, page_size: pageSize })}`
    );
    const data = response?.data ?? response ?? {};

    const quests = Array.isArray(data) ? data : data.results || [];

    quests.sort((a, b) => a.id - b.id);

    yield put({
      type: types.RECEIVE_QUESTS,
      quests,
    });
  } catch (error) {
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* receiveQuests(action) {
  const pathname = yield select((state) => state.router.location.pathname);
  const isFetching = yield select((state) => state.quests.isFetching);
  const { quests } = action;

  try {
    if (pathname === '/' && !isFetching) {
      const queryParams = queryString(window.location.search);
      const { openLastTourCreated } = queryParams;
      if (openLastTourCreated && quests.length) {
        yield put({
          type: types.SELECT_QUEST,
          id: quests[quests.length - 1].id,
          redirect: true,
        });
      }
      yield call(CachedTutorial.getTutorialDataFromCache, FirstSteps);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.log(error);
    yield put({
      type: types.RESET_TUTORIAL,
    });
  }
}

export function* addQuest(payload) {
  const {
    title,
    eventTitles = [],
    events = [],
    redirect = {},
    selecting = true,
  } = payload;
  try {
    const {
      data: { id },
    } = yield call(Api.post, 'quests/', {
      data: {
        title: title || '',
        events,
      },
    });
    yield put({ type: types.CREATE_QUEST, id, title });
    yield call(fetchQuest, { id });

    if (!events.length) {
      yield all(
        eventTitles.map((eventTitle) =>
          put(
            addEvent({
              questId: id,
              title: eventTitle,
              time: 0,
              sync: false,
            })
          )
        )
      );
      yield call(syncQuest, { id, updatePlans: true });
    }

    let idProduct = yield select(
      (state) => getQuestById(state.quests.quests, id).products[0].id
    );

    while (!idProduct) {
      idProduct = yield select(
        (state) => getQuestById(state.quests.quests, id).products[0].id
      );
    }

    const locale = yield select((state) => state.user.locale);

    yield put(
      postListedData(
        idProduct,
        {
          text:
            locale === 'ru'
              ? 'Наушники — пожалуйста, возьмите свои'
              : 'Earphones — please bring your own',
        },
        'exclusions'
      )
    );

    if (selecting) {
      yield put({ type: types.SELECT_QUEST, id, sync: false, redirect: true });
    }
    yield put({ type: types.CREATE_QUEST_SUCCESS, redirect });
    yield put({
      type: types.ANALYTICS_TRACK_EVENT,
      event: 'create_quest',
      eventCategory: 'Authors',
      eventAction: 'Create Quest',
      title,
    });
  } catch (error) {
    yield put({ type: types.CREATE_QUEST_FAILURE, error });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* handleCreateQuestSuccess({ redirect }) {
  if (isEmpty(redirect)) return;
  if (redirect.isHard) {
    window.location = redirect.url;
  } else {
    yield put(push(redirect.url));
  }
}

export function* selectQuest(action) {
  const { redirect, sync = true } = action;
  const id = parseInt(action.id, 10);

  try {
    if (sync) {
      yield call(fetchQuest, { id, redirect });
    }
    if (redirect) {
      yield put(push(`/quest/${id}/title/`));
    }
  } catch (error) {
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* deleteQuest(action) {
  const { id, productId } = action;
  try {
    yield call(Api.delete, `quests/${id}/`);
    yield put({
      type: types.DELETE_QUEST_SUCCESS,
      id,
      productId,
    });
    const currentPage = yield select(
      (state) => state.stats.questsStats.current
    );
    yield put({
      type: types.REQUEST_QUESTS_STATS,
      page: currentPage,
    });
  } catch (error) {
    yield put({
      type: types.DELETE_QUEST_FAILURE,
      id,
      productId,
      error,
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}

export function* copyQuest(action) {
  const { id, title, redirect } = action;
  try {
    const {
      data: { id: copyId },
    } = yield call(Api.post, `quests/${id}/copy/`, {
      data: {
        id,
        title,
      },
    });
    yield* fetchQuest({
      id: copyId,
      redirect,
    });
    yield put({
      type: types.COPY_QUEST_SUCCESS,
      id,
    });
  } catch (error) {
    yield put({
      type: types.COPY_QUEST_FAILURE,
      id,
    });
    yield put({ type: types.NETWORK_ERROR, error });
  }
}
